/**
 * Writes a new case, or a new version of one starting from a saved version. Nothing is
 * overwritten: the API saves a new version next to the others.
 */
import { useState } from 'react';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import type { EvalCaseContent, EvalCaseVersion } from '../../types/evalTypes';
import { CaseContentEditor, type CaseInputExpect } from './CaseContentEditors';

const CASE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;
const MAX_NOTE_CHARS = 2000;

export interface CaseDraft {
  caseId: string;
  content: EvalCaseContent;
  note: string;
  makeCurrent: boolean;
}

interface CaseEditorProps {
  suiteId: string;
  /** The suite's case options, for its form. */
  caseOptions: Record<string, string[]>;
  /** The version to start from. Undefined for a new case. */
  from?: { caseId: string; version: EvalCaseVersion };
  /** Throws on failure, so the editor can show the error. */
  onSave: (draft: CaseDraft) => Promise<void>;
  onCancel: () => void;
}

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50';

const toTags = (text: string): string[] => [
  ...new Set(
    text
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag) => tag !== ''),
  ),
];

export const CaseEditor = ({ suiteId, caseOptions, from, onSave, onCancel }: CaseEditorProps) => {
  const initial: CaseInputExpect | null = from ? { input: from.version.input, expect: from.version.expect } : null;
  const [caseId, setCaseId] = useState(from?.caseId ?? '');
  const [description, setDescription] = useState(from?.version.description ?? '');
  const [tags, setTags] = useState(from?.version.tags.join(', ') ?? '');
  const [inputExpect, setInputExpect] = useState<CaseInputExpect | null>(initial);
  const [note, setNote] = useState('');
  // A new case is meant to run; a new version waits until someone makes it current.
  const [makeCurrent, setMakeCurrent] = useState(!from);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const content: EvalCaseContent | null = inputExpect && { description: description.trim(), tags: toTags(tags), ...inputExpect };
  const unchanged =
    from !== undefined &&
    content !== null &&
    JSON.stringify(content) === JSON.stringify({ description: from.version.description, tags: from.version.tags, input: from.version.input, expect: from.version.expect });
  const idProblem = !from && caseId !== '' && !CASE_ID_PATTERN.test(caseId) ? 'Use lowercase letters, digits and "-".' : null;

  const save = async () => {
    if (!content) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ caseId, content, note: note.trim(), makeCurrent });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to save the case.');
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {from ? `New version of ${from.caseId}, starting from v${from.version.number}.` : 'A new case. Synthetic data only: never paste a real chat.'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[16rem_minmax(0,1fr)_16rem] gap-3">
        <label>
          <span className="block mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Case ID</span>
          <input
            value={caseId}
            disabled={saving || from !== undefined}
            onChange={(event) => setCaseId(event.target.value.trim())}
            placeholder="stove-checking"
            className={`${fieldClass} font-mono`}
          />
          {idProblem && <span className="block mt-1 text-xs text-red-700">{idProblem}</span>}
        </label>
        <label>
          <span className="block mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Description</span>
          <input
            value={description}
            disabled={saving}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What the case tests, in one line."
            className={fieldClass}
          />
        </label>
        <label>
          <span className="block mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Tags</span>
          <input value={tags} disabled={saving} onChange={(event) => setTags(event.target.value)} placeholder="checking, risk" className={fieldClass} />
        </label>
      </div>

      <CaseContentEditor suiteId={suiteId} initial={initial} options={caseOptions} disabled={saving} onChange={setInputExpect} />

      <input
        value={note}
        maxLength={MAX_NOTE_CHARS}
        disabled={saving}
        onChange={(event) => setNote(event.target.value)}
        placeholder="What changed, and why? For example: split the work fact in two, the judge failed it on a detail."
        className={fieldClass}
      />
      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={makeCurrent} disabled={saving} onChange={(event) => setMakeCurrent(event.target.checked)} />
        Make it current: runs queued from now on use it
      </label>

      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      <div className="flex items-center justify-end gap-2">
        {unchanged && <span className="text-sm text-gray-500">No changes yet.</span>}
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void save()}
          disabled={saving || !content || unchanged || caseId === '' || idProblem !== null || description.trim() === ''}
          loading={saving}
        >
          {from ? 'Save version' : 'Save case'}
        </Button>
      </div>
    </div>
  );
};
